import { useState, useEffect, useRef } from "react"
import { motion, AnimatePresence, useReducedMotion } from "framer-motion"

const INPUT_SOURCES = [
  { id: "email", label: "Lead form: acme.com" },
  { id: "invoice", label: "Invoice #4821.pdf" },
  { id: "ticket", label: "Support ticket #112" },
]

const TOOL_CHIPS = [
  { id: "gmail", label: "G", name: "Gmail" },
  { id: "slack", label: "S", name: "Slack" },
  { id: "notion", label: "N", name: "Notion" },
  { id: "hubspot", label: "H", name: "HubSpot" },
  { id: "stripe", label: "$", name: "Stripe" },
  { id: "postgres", label: "DB", name: "Postgres" },
]

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false)

  useEffect(() => {
    const media = window.matchMedia(query)
    setMatches(media.matches)

    const listener = (event: MediaQueryListEvent) => setMatches(event.matches)
    media.addEventListener("change", listener)
    return () => media.removeEventListener("change", listener)
  }, [query])

  return matches
}

type Particle = {
  id: string
  sourceId: string
  targetChipIds: string[]
  startTime: number
}

export function HeroAutomationWorkbench() {
  const prefersReducedMotion = useReducedMotion()
  const isMobile = useMediaQuery("(max-width: 640px)")
  const [mounted, setMounted] = useState(false)
  const [activeCardIndex, setActiveCardIndex] = useState<number | null>(null)
  const [processingCard, setProcessingCard] = useState<string | null>(null)
  const [particles, setParticles] = useState<Particle[]>([])
  const [highlightedChips, setHighlightedChips] = useState<Set<string>>(new Set())
  const [checkedChips, setCheckedChips] = useState<Set<string>>(new Set())

  const [tasksAutomated, setTasksAutomated] = useState(1200)
  const [responseTime, setResponseTime] = useState("4.2s")
  const [hoursSaved, setHoursSaved] = useState(38)

  const [leadProgress, setLeadProgress] = useState(0)

  const animationCycle = useRef(0)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!mounted || prefersReducedMotion) return

    const cardCycleInterval = setInterval(() => {
      const sourceIndex = animationCycle.current % INPUT_SOURCES.length
      const source = INPUT_SOURCES[sourceIndex]

      setActiveCardIndex(sourceIndex)

      setTimeout(() => {
        setActiveCardIndex(null)
        setProcessingCard(source.id)

        setTimeout(() => {
          setProcessingCard(null)

          const numTargets = 2 + Math.floor(Math.random() * 2)
          const shuffledChips = [...TOOL_CHIPS].sort(() => Math.random() - 0.5)
          const targetChipIds = shuffledChips.slice(0, numTargets).map(c => c.id)

          const newParticle: Particle = {
            id: `${source.id}-${Date.now()}`,
            sourceId: source.id,
            targetChipIds,
            startTime: Date.now(),
          }

          setParticles(prev => [...prev, newParticle])

          setTimeout(() => {
            setHighlightedChips(new Set(targetChipIds))
            setCheckedChips(prev => new Set([...prev, ...targetChipIds]))

            setTimeout(() => {
              setHighlightedChips(new Set())
            }, 300)

            setTimeout(() => {
              setCheckedChips(prev => {
                const newSet = new Set(prev)
                targetChipIds.forEach(id => newSet.delete(id))
                return newSet
              })
            }, 1000)

            setParticles(prev => prev.filter(p => p.id !== newParticle.id))
          }, 800)
        }, 500)
      }, 1500)

      animationCycle.current++
    }, 2000)

    return () => clearInterval(cardCycleInterval)
  }, [mounted, prefersReducedMotion])

  useEffect(() => {
    if (!mounted || prefersReducedMotion) return

    const tasksInterval = setInterval(() => {
      setTasksAutomated(prev => prev + 1)
    }, 1200)

    const responseInterval = setInterval(() => {
      setResponseTime(prev => prev === "4.2s" ? "4.8s" : "4.2s")
    }, 2500)

    const hoursInterval = setInterval(() => {
      setHoursSaved(prev => prev + 1)
    }, 6000)

    return () => {
      clearInterval(tasksInterval)
      clearInterval(responseInterval)
      clearInterval(hoursInterval)
    }
  }, [mounted, prefersReducedMotion])

  useEffect(() => {
    if (!mounted || prefersReducedMotion) return

    const progressInterval = setInterval(() => {
      setLeadProgress(prev => {
        if (prev >= 100) return 0
        return prev + 2
      })
    }, 100)

    return () => clearInterval(progressInterval)
  }, [mounted, prefersReducedMotion])

  const getChipPosition = (index: number, mobile: boolean) => {
    if (mobile) {
      const row = Math.floor(index / 3)
      const col = index % 3
      return { x: 180 + col * 120, y: 440 + row * 80 }
    } else {
      return { x: 60 + index * 100, y: 480 }
    }
  }

  if (!mounted || prefersReducedMotion) {
    return (
      <div className="w-full max-w-[720px] mx-auto" style={{ aspectRatio: "720/680" }}>
        <svg
          viewBox="0 0 720 680"
          className="w-full h-auto"
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label="AI automation workbench showing inbox items being processed by AI agent and distributed to connected tools"
        >
          <rect x="0" y="0" width="720" height="680" rx="16" fill="white" stroke="#E2E8F0" strokeWidth="2" />

          <g transform="translate(260, 50)">
            <rect x="0" y="0" width="200" height="60" rx="10" fill="white" stroke="#E2E8F0" strokeWidth="1.5" />
            <g transform="translate(16, 18)">
              <rect width="24" height="24" rx="6" fill="#FEE2E2" />
              <path d="M4 6h16v12H4V6zm0 0l8 6 8-6" stroke="#ed2132" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </g>
            <text x="52" y="36" fontSize="13" fill="#0F172A" fontWeight="600">Lead form: acme.com</text>
            <text x="52" y="50" fontSize="10" fill="#64748B">Processing...</text>
          </g>

          <g transform="translate(360, 240)">
            <circle cx="0" cy="0" r="48" fill="white" stroke="#FECACA" strokeWidth="2" />
            <rect x="-16" y="-12" width="32" height="24" rx="6" fill="#ed2132" />
            <circle cx="-6" cy="-2" r="3" fill="white" />
            <circle cx="6" cy="-2" r="3" fill="white" />
            <rect x="-8" y="6" width="16" height="4" rx="2" fill="white" />
            <text x="0" y="-70" textAnchor="middle" fontSize="16" fontWeight="700" fill="#0F172A">AI Agent</text>
            <text x="0" y="-54" textAnchor="middle" fontSize="12" fill="#64748B">Claude · GPT-4</text>
          </g>

          <g>
            {TOOL_CHIPS.map((chip, i) => {
              const pos = getChipPosition(i, false)
              return (
                <g key={chip.id} transform={`translate(${pos.x}, ${pos.y})`}>
                  <rect x="0" y="0" width="56" height="56" rx="12" fill="white" stroke="#E2E8F0" strokeWidth="1.5" />
                  <text x="28" y="34" textAnchor="middle" fontSize="16" fontWeight="700" fill="#475569">{chip.label}</text>
                  <text x="28" y="72" textAnchor="middle" fontSize="10" fill="#94A3B8">{chip.name}</text>
                  <g transform="translate(42, -6)">
                    <circle cx="8" cy="8" r="10" fill="#22C55E" />
                    <path d="M4 8l3 3 5-6" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </g>
                </g>
              )
            })}
          </g>

          <g transform="translate(504, 16)">
            <rect x="0" y="0" width="200" height="130" rx="10" fill="white" stroke="#E2E8F0" strokeWidth="1.5" />
            <circle cx="16" cy="20" r="5" fill="#22C55E" />
            <text x="28" y="24" fontSize="12" fontWeight="600" fill="#0F172A">Live</text>
            <text x="16" y="50" fontSize="11" fill="#64748B">Tasks automated today</text>
            <text x="16" y="70" fontSize="18" fontWeight="700" fill="#0F172A" fontFamily="monospace">1,247</text>
            <text x="16" y="92" fontSize="11" fill="#64748B">Avg response time</text>
            <text x="16" y="112" fontSize="18" fontWeight="700" fill="#0F172A" fontFamily="monospace">4.2s</text>
          </g>

          <g transform="translate(16, 560)">
            <text x="0" y="0" fontSize="12" fontWeight="600" fill="#475569">Lead response window</text>
            <rect x="0" y="10" width="260" height="10" rx="5" fill="#F1F5F9" />
            <rect x="0" y="10" width="260" height="10" rx="5" fill="#ed2132" />
            <g transform="translate(280, 0)">
              <rect x="0" y="4" width="160" height="28" rx="14" fill="#FEE2E2" />
              <text x="80" y="24" textAnchor="middle" fontSize="12" fontWeight="600" fill="#ed2132">21x more qualified leads</text>
            </g>
          </g>
        </svg>
      </div>
    )
  }

  return (
    <div className="w-full max-w-[720px] mx-auto" style={{ aspectRatio: "720/680" }}>
      <svg
        viewBox="0 0 720 680"
        width="100%"
        height="auto"
        role="img"
        aria-label="Animated AI automation workbench showing email, document, and ticket inputs being processed by an AI agent and routed to connected tools, with live metrics."
      >
        <rect x="0" y="0" width="720" height="680" rx="16" fill="white" stroke="#E2E8F0" strokeWidth="2" />

        <defs>
          <radialGradient id="aiGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#ed2132" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#ed2132" stopOpacity="0" />
          </radialGradient>
        </defs>

        <g stroke="#CBD5E1" strokeWidth="1.5" strokeDasharray="4 4" fill="none">
          <path d="M 360 140 L 360 192" />
          {TOOL_CHIPS.map((chip, i) => {
            const pos = getChipPosition(i, isMobile)
            const chipCenterX = pos.x + 28
            const chipCenterY = pos.y
            return (
              <path
                key={chip.id}
                d={`M 360 288 Q 360 ${(288 + chipCenterY) / 2} ${chipCenterX} ${chipCenterY}`}
              />
            )
          })}
        </g>

        <g transform="translate(0, 20)">
          <text x="60" y="20" fontSize={isMobile ? 14 : 12} fontWeight="700" fill="#475569">INBOX</text>

          <g transform="translate(60, 35)">
            {INPUT_SOURCES.map((source, i) => {
              const isActive = activeCardIndex === i
              const cardWidth = 200
              const centerX = (600 - cardWidth) / 2 - 60

              return (
                <AnimatePresence key={source.id}>
                  {isActive && (
                    <motion.g
                      initial={{ x: -cardWidth - 20, opacity: 0 }}
                      animate={{ x: centerX, opacity: 1 }}
                      exit={{ y: 50, opacity: 0 }}
                      transition={{
                        x: { duration: 1.2, ease: "easeOut" },
                        y: { duration: 0.3, ease: "easeIn" },
                        opacity: { duration: 0.3 }
                      }}
                    >
                      <rect x="0" y="0" width={cardWidth} height="60" rx="10" fill="white" stroke="#E2E8F0" strokeWidth="1.5" />
                      <g transform="translate(16, 18)">
                        <rect width="24" height="24" rx="6" fill="#FEE2E2" />
                        {source.id === "email" && (
                          <path d="M4 6h16v12H4V6zm0 0l8 6 8-6" stroke="#ed2132" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        )}
                        {source.id === "invoice" && (
                          <path d="M6 2h9l5 5v13H6V2zm6 0v6h6M9 11h6M9 15h4" stroke="#ed2132" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" transform="translate(-2, 0)" />
                        )}
                        {source.id === "ticket" && (
                          <path d="M4 4h16v14H4V4zM8 9h8M8 13h5" stroke="#ed2132" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                        )}
                      </g>
                      <text x="52" y={isMobile ? 34 : 32} fontSize={isMobile ? 14 : 13} fill="#0F172A" fontWeight="600">{source.label}</text>
                      <text x="52" y={isMobile ? 50 : 48} fontSize={isMobile ? 12 : 10} fill="#64748B">New input</text>
                    </motion.g>
                  )}
                </AnimatePresence>
              )
            })}
          </g>
        </g>

        <g transform="translate(360, 240)">
          <circle cx="0" cy="0" r="80" fill="url(#aiGlow)" />

          <motion.circle
            cx="0"
            cy="0"
            r="48"
            fill="none"
            stroke="#FECACA"
            strokeWidth="2"
            initial={{ scale: 1, opacity: 0.8 }}
            animate={{ scale: [1, 1.4, 1.4], opacity: [0.8, 0, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeOut" }}
          />

          <motion.circle
            cx="0"
            cy="0"
            r="48"
            fill="white"
            stroke="#FECACA"
            strokeWidth="2"
            animate={processingCard ? { scale: [1, 1.1, 1] } : { scale: 1 }}
            transition={{ duration: 0.3 }}
          />

          <g transform="translate(-16, -12)">
            <rect x="0" y="0" width="32" height="24" rx="6" fill="#ed2132" />
            <circle cx="10" cy="10" r="3" fill="white" />
            <circle cx="22" cy="10" r="3" fill="white" />
            <rect x="8" y="16" width="16" height="4" rx="2" fill="white" />
          </g>

          <text x="0" y="-70" textAnchor="middle" fontSize={isMobile ? 18 : 16} fontWeight="700" fill="#0F172A">AI Agent</text>
          <text x="0" y="-52" textAnchor="middle" fontSize={isMobile ? 14 : 12} fill="#64748B">Claude · GPT-4</text>
        </g>

        <AnimatePresence>
          {particles.map(particle =>
            particle.targetChipIds.map((chipId, idx) => {
              const chipIndex = TOOL_CHIPS.findIndex(c => c.id === chipId)
              const pos = getChipPosition(chipIndex, isMobile)
              const targetX = pos.x + 28
              const targetY = pos.y + 28

              return (
                <motion.circle
                  key={`${particle.id}-${chipId}`}
                  r="6"
                  fill="#ed2132"
                  initial={{ cx: 360, cy: 288, opacity: 1 }}
                  animate={{ cx: targetX, cy: targetY, opacity: [1, 1, 0] }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8, delay: idx * 0.1, ease: "easeOut" }}
                />
              )
            })
          )}
        </AnimatePresence>

        <g>
          <text
            x="360"
            y={isMobile ? 420 : 460}
            textAnchor="middle"
            fontSize={isMobile ? 14 : 12}
            fontWeight="700"
            fill="#475569"
          >
            CONNECTED TOOLS
          </text>

          {TOOL_CHIPS.map((chip, i) => {
            const pos = getChipPosition(i, isMobile)
            const isHighlighted = highlightedChips.has(chip.id)
            const isChecked = checkedChips.has(chip.id)

            return (
              <g key={chip.id} transform={`translate(${pos.x}, ${pos.y})`}>
                <motion.rect
                  x="0"
                  y="0"
                  width="56"
                  height="56"
                  rx="12"
                  fill={isHighlighted ? "#FEE2E2" : "white"}
                  stroke="#E2E8F0"
                  strokeWidth="1.5"
                  animate={{ fill: isHighlighted ? "#FEE2E2" : "white" }}
                  transition={{ duration: 0.3 }}
                />
                <text x="28" y="34" textAnchor="middle" fontSize={isMobile ? 18 : 16} fontWeight="700" fill="#475569">{chip.label}</text>
                <text x="28" y="72" textAnchor="middle" fontSize={isMobile ? 11 : 10} fill="#94A3B8">{chip.name}</text>

                <AnimatePresence>
                  {isChecked && (
                    <motion.g
                      transform="translate(42, -6)"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <circle cx="8" cy="8" r="10" fill="#22C55E" />
                      <path d="M4 8l3 3 5-6" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                    </motion.g>
                  )}
                </AnimatePresence>
              </g>
            )
          })}
        </g>

        <g transform={isMobile ? "translate(260, 320)" : "translate(504, 16)"}>
          <rect x="0" y="0" width="200" height="130" rx="10" fill="white" stroke="#E2E8F0" strokeWidth="1.5" />
          <circle cx="16" cy="20" r="5" fill="#22C55E" />
          <text x="28" y="24" fontSize={isMobile ? 14 : 12} fontWeight="600" fill="#0F172A">Live</text>

          <text x="16" y="50" fontSize={isMobile ? 12 : 11} fill="#64748B">Tasks automated today</text>
          <text x="16" y="72" fontSize={isMobile ? 20 : 18} fontWeight="700" fill="#0F172A" fontFamily="monospace">{tasksAutomated.toLocaleString()}</text>

          <text x="16" y="96" fontSize={isMobile ? 12 : 11} fill="#64748B">Avg response time</text>
          <text x="16" y="118" fontSize={isMobile ? 20 : 18} fontWeight="700" fill="#0F172A" fontFamily="monospace">{responseTime}</text>

          <text x="120" y="96" fontSize={isMobile ? 12 : 11} fill="#64748B">Hours saved</text>
          <text x="120" y="118" fontSize={isMobile ? 20 : 18} fontWeight="700" fill="#0F172A" fontFamily="monospace">{hoursSaved}</text>
        </g>

        <g transform={isMobile ? "translate(60, 620)" : "translate(16, 620)"}>
          <text x="0" y="0" fontSize={isMobile ? 14 : 12} fontWeight="600" fill="#475569">Lead response window</text>

          <rect x="0" y="12" width={isMobile ? 600 : 260} height="12" rx="6" fill="#F1F5F9" />

          <motion.rect
            x="0"
            y="12"
            height="12"
            rx="6"
            fill="#ed2132"
            animate={{ width: (isMobile ? 600 : 260) * (leadProgress / 100) }}
            transition={{ duration: 0.1 }}
          />

          <g transform={isMobile ? "translate(200, 32)" : "translate(280, 0)"}>
            <rect x="0" y="4" width="160" height="28" rx="14" fill="#FEE2E2" />
            <text x="80" y="24" textAnchor="middle" fontSize={isMobile ? 13 : 12} fontWeight="600" fill="#ed2132">21x more qualified leads</text>
          </g>
        </g>
      </svg>
    </div>
  )
}
